/*
## Summary
Authentication provider context composable. Exposes user state and auth status via provide/inject pattern.

## Responsibilities
- Create and manage authentication provider context
- Expose current authenticated user reactively
- Track authentication loading and error states
- Provide auth context to child components
- Manage user session lifecycle

## Out of Scope
- Login/signup mechanics (delegated to Auth.ts)
- Session initialization on app startup (separate init)
- User profile fetching (delegated to services)
- Permission and role-based access control
- Session persistence

## Dependencies
- Vue 3 (ref, provide/inject)
- Supabase auth types
- Auth composable for authentication
- Type definitions for User

## Notes
Provides auth state globally via Vue's provide/inject API.
*/
import { ref } from 'vue'
import { supabase } from '@/clients/supabase'
import { useAppCache } from './useAppCache'
import { useMessagesCache } from './useMessagesCache'
import type { User, UserSession } from '@/types'

const USER_PROFILE_TTL_MS = 5 * 60_000

const currentUser = ref<User | null>(null) // profil complet (table users)
const currentSession = ref<UserSession | null>(null)
// indique si le chargement de l'authentification est terminé
const authLoaded = ref(false)
let loadSessionPromise: Promise<void> | null = null
let loadSessionRequestId = 0

export function useAuth() {
  const cache = useAppCache()

  // charge la session et le profil utilisateur depuis Supabase
  async function loadSessionFromSupabase(options: { force?: boolean } = {}) {
    if (loadSessionPromise && !options.force) {
      return loadSessionPromise
    }

    const requestId = ++loadSessionRequestId
    authLoaded.value = false

    loadSessionPromise = (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (requestId !== loadSessionRequestId) return

      currentSession.value = session as UserSession | null

      if (!session) {
        currentUser.value = null
        authLoaded.value = true
        return
      }

      const cacheKey = `user:profile:${session.user.id}`
      const cached = cache.get<User>(cacheKey)
      // The auth check and profile read depend on the session, but not on each
      // other. Keep the verified user check before publishing either result.
      const [authResult, profileResult] = await Promise.all([
        supabase.auth.getUser(),
        cached
          ? Promise.resolve(null)
          : supabase.from('users').select('*').eq('id', session.user.id).single(),
      ])

      if (requestId !== loadSessionRequestId) return

      const userFromDb = authResult.data.user
      if (!userFromDb || userFromDb.id !== session.user.id) {
        currentUser.value = null
        authLoaded.value = true
        return
      }

      // Return cached profile when available (5 min TTL, memory-only — never persisted)
      if (cached) {
        currentUser.value = cached
        authLoaded.value = true
        return
      }

      const user = profileResult?.data
      const userError = profileResult?.error

      if (requestId !== loadSessionRequestId) return

      if (userError || !user) {
        currentUser.value = null
        authLoaded.value = true
        return
      }

      cache.set(cacheKey, user as User, { ttlMs: USER_PROFILE_TTL_MS, persist: false })
      currentUser.value = user as User | null
      authLoaded.value = true
    })().finally(() => {
      if (requestId === loadSessionRequestId) {
        loadSessionPromise = null
      }
    })

    return loadSessionPromise
  }

  /** Drop all cached state (memory + mac: localStorage). Call on logout. */
  function clearSession(): void {
    useMessagesCache().invalidateAll()
    cache.clear()
    currentUser.value = null
    currentSession.value = null
    authLoaded.value = false
    loadSessionPromise = null
    loadSessionRequestId++
  }

  return {
    currentUser,
    currentSession,
    authLoaded,
    loadSessionFromSupabase,
    clearSession,
  }
}
